import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiPanel,
  EuiTitle,
  EuiSpacer,
  EuiText,
  EuiBadge,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiHorizontalRule,
  EuiCallOut,
  EuiCode,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { useApi } from "src/api";
import { useAnketaAuth } from "src/auth";
import { AnketaResourcesEnum } from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useOpsanioCan } from "../../can";
import { FormSkeleton } from "src/components/skeleton";
import { getFeatures } from "src/helper/parser/opsanio";

const i18n = "anketa.opsanio.appliance.flyout.oidc";

/**
 * Kubernetes OIDC client status + provisioning. Checks whether the
 * `<name>-kubernetes` Keycloak client exists; if not, offers a button that has
 * anketa create it and persist the secret to Vault (kube-config-oidc). The next
 * S3 render returns it and Flux generates the oidc-connection-data secret -
 * replacing 210-k3s oidc.tf.
 */
const KubernetesOidcCard = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  // Creating the Keycloak client: opsanio/appliance · create.
  const canCreateClient = useOpsanioCan()(
    AnketaResourcesEnum.opsanioAppliance,
    "create",
  );
  const { translate } = useAnketaI18n();

  const meta = appliance.common?.metadata;
  const instanceName = meta?.name?.trim() ?? "";

  const [exists, setExists] = useState<boolean | null>(null);
  const [clientId, setClientId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string" && instanceName !== "",
    [keycloak, instanceName],
  );

  const check = useCallback(async () => {
    if (!canFetch) return;
    setLoading(true);
    setError(null);
    try {
      const { success, data, error: err } = await Get<any>({
        path: `v2/${AnketaResourcesEnum.opsanio}/keycloak/oidc-client/${encodeURIComponent(
          instanceName,
        )}`,
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || err) {
        setError(
          (err as any)?.message ??
            translate(`${i18n}.error`, {
              defaultMessage: "Could not query the OIDC client status.",
            }),
        );
        setExists(null);
      } else {
        setExists(!!(data as any)?.data?.exists);
        setClientId((data as any)?.data?.clientId ?? "");
      }
    } catch {
      setError(
        translate(`${i18n}.error`, {
          defaultMessage: "Could not query the OIDC client status.",
        }),
      );
      setExists(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, instanceName, keycloak, translate]);

  useEffect(() => {
    check();
  }, [check]);

  const create = useCallback(async () => {
    if (!canFetch || !meta) return;
    setCreating(true);
    setError(null);
    try {
      const { success, error: err } = await Post<any>({
        path: `v2/${AnketaResourcesEnum.opsanio}/keycloak/oidc-client/${encodeURIComponent(
          instanceName,
        )}`,
        value: { owner: meta.owner, project: meta.project, stage: meta.stage },
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || err) {
        setError(
          (err as any)?.message ??
            translate(`${i18n}.createError`, {
              defaultMessage: "Failed to create the OIDC client.",
            }),
        );
      } else {
        await check();
      }
    } catch {
      setError(
        translate(`${i18n}.createError`, {
          defaultMessage: "Failed to create the OIDC client.",
        }),
      );
    } finally {
      setCreating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, meta, instanceName, keycloak, translate, check]);

  const header = (
    <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
      <EuiFlexItem grow={false}>
        <EuiIcon type="lock" size="l" />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiTitle size="s">
          <h3 style={{ margin: 0 }}>
            {translate(`${i18n}.title`, {
              defaultMessage: "Kubernetes OIDC client",
            })}
          </h3>
        </EuiTitle>
      </EuiFlexItem>
      {clientId !== "" && (
        <EuiFlexItem grow={false}>
          <EuiBadge color="hollow">{clientId}</EuiBadge>
        </EuiFlexItem>
      )}
      <EuiFlexItem grow={false}>
        <EuiButton
          size="s"
          iconType="refresh"
          onClick={check}
          isLoading={loading}
          disabled={!canFetch}
        >
          {translate("anketa.opsanio.refresh", { defaultMessage: "Refresh" })}
        </EuiButton>
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const wrap = (body: React.ReactNode) => (
    <EuiPanel paddingSize="m" hasShadow={false} color="subdued">
      {header}
      <EuiHorizontalRule margin="m" />
      {body}
    </EuiPanel>
  );

  if (instanceName === "") {
    return wrap(
      <EuiCallOut
        color="warning"
        iconType="help"
        title={translate(`${i18n}.noInstance.title`, {
          defaultMessage: "No cluster name",
        })}
      />,
    );
  }

  if (loading && exists === null) return wrap(<FormSkeleton />);

  return wrap(
    <>
      {error && (
        <>
          <EuiCallOut color="danger" iconType="alert" title={error} />
          <EuiSpacer size="m" />
        </>
      )}
      {exists === true && (
        <EuiCallOut
          color="success"
          iconType="check"
          title={translate(`${i18n}.exists.title`, {
            defaultMessage: "OIDC client exists",
          })}
        >
          <EuiText size="s">
            {translate(`${i18n}.exists.body`, {
              defaultMessage:
                "The Keycloak client {{clientId}} is provisioned. Its secret is in Vault and returned to the cluster over S3.",
              values: { clientId },
            })}
          </EuiText>
        </EuiCallOut>
      )}
      {exists === false && (
        <EuiCallOut
          color="warning"
          iconType="alert"
          title={translate(`${i18n}.missing.title`, {
            defaultMessage: "OIDC client not found",
          })}
        >
          <EuiText size="s">
            {translate(`${i18n}.missing.body`, {
              defaultMessage:
                "No Keycloak client {{clientId}} exists yet. Create it to enable cluster OIDC login.",
              values: { clientId },
            })}
          </EuiText>
          <EuiSpacer size="m" />
          {canCreateClient && (
          <EuiButton
            fill
            iconType="plusInCircle"
            onClick={create}
            isLoading={creating}
            disabled={!canFetch}
          >
            {translate(`${i18n}.create`, {
              defaultMessage: "Create OIDC client",
            })}
          </EuiButton>
          )}
        </EuiCallOut>
      )}
      <EuiSpacer size="m" />
      <EuiText size="xs" color="subdued">
        {translate(`${i18n}.note`, {
          defaultMessage:
            "Creating writes the secret to Vault at",
        })}{" "}
        <EuiCode>
          {`vpc/${meta?.owner}/${meta?.project}/${meta?.stage}/kube-config-oidc`}
        </EuiCode>
      </EuiText>
    </>,
  );
};

/**
 * The AI gateway's Keycloak client (`<name>-litellm`), and the group bindings
 * that make it mean something.
 *
 * This is where the gateway's access control actually lives. Pangolin publishes
 * the LiteLLM UI as a plain tunnel - no policy, no SSO at the edge - because an
 * edge policy can only say "in" or "out". LiteLLM logs the user in against
 * Keycloak and reads the `litellm_role` claim, so membership of
 * `<name>/ai-admins` or `<name>/ai-users` is what decides whether a session may
 * mint keys and read every consumer's spend, or only its own.
 *
 * Creating is idempotent and also (re)creates the `proxy_admin` /
 * `internal_user` client roles and their group bindings. Rendered only for an
 * AI-enabled appliance: on any other VPC the client is absent by design.
 */
const LiteLlmOidcCard = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element | null => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  // Creating the Keycloak client: opsanio/appliance · create.
  const canCreateClient = useOpsanioCan()(
    AnketaResourcesEnum.opsanioAppliance,
    "create",
  );
  const { translate } = useAnketaI18n();

  const instanceName = appliance.common?.metadata?.name?.trim() ?? "";
  const aiEnabled = useMemo<boolean>(() => {
    try {
      return (
        getFeatures(appliance.content, { private: "", public: "" }).ai === true
      );
    } catch {
      return false;
    }
  }, [appliance.content]);

  const [exists, setExists] = useState<boolean | null>(null);
  const [clientId, setClientId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string" && instanceName !== "",
    [keycloak, instanceName],
  );

  const check = useCallback(async () => {
    if (!canFetch || !aiEnabled) return;
    setLoading(true);
    setError(null);
    const resp = await Get<any>({
      path: `v2/${AnketaResourcesEnum.opsanio}/keycloak/litellm-client/${encodeURIComponent(
        instanceName,
      )}`,
      responseType: "json",
      token: keycloak.token,
    });
    // useApi resolves with success:false on an HTTP error rather than throwing.
    if (!resp?.success) {
      setError(
        translate(`${i18n}.litellm.error`, {
          defaultMessage: "Could not query the LiteLLM client status.",
        }),
      );
      setExists(null);
    } else {
      setExists(!!(resp.data as any)?.data?.exists);
      setClientId((resp.data as any)?.data?.clientId ?? "");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, aiEnabled, instanceName, keycloak, translate]);

  useEffect(() => {
    check();
  }, [check]);

  const create = useCallback(async () => {
    if (!canFetch) return;
    setCreating(true);
    setError(null);
    const resp = await Post<any>({
      path: `v2/${AnketaResourcesEnum.opsanio}/keycloak/litellm-client/${encodeURIComponent(
        instanceName,
      )}`,
      value: {},
      responseType: "json",
      token: keycloak.token,
    });
    if (!resp?.success) {
      setError(
        translate(`${i18n}.litellm.createError`, {
          defaultMessage: "Failed to create the LiteLLM OIDC client.",
        }),
      );
    } else {
      await check();
    }
    setCreating(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, instanceName, keycloak, translate, check]);

  // Nothing to show on a VPC without the AI stack - the client is absent by
  // design there, and a "missing" card would read as a gap.
  if (!aiEnabled) return null;

  const redirectUri = `https://litellm-${instanceName}.pangolin.exanio.cloud/sso/callback`;

  return (
    <EuiPanel paddingSize="m" hasShadow={false} color="subdued">
      <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiIcon type="lock" size="l" />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiTitle size="s">
            <h3 style={{ margin: 0 }}>
              {translate(`${i18n}.litellm.title`, {
                defaultMessage: "LiteLLM OIDC client",
              })}
            </h3>
          </EuiTitle>
        </EuiFlexItem>
        {clientId !== "" && (
          <EuiFlexItem grow={false}>
            <EuiBadge color="hollow">{clientId}</EuiBadge>
          </EuiFlexItem>
        )}
        <EuiFlexItem grow={false}>
          <EuiButton
            size="s"
            iconType="refresh"
            onClick={check}
            isLoading={loading}
            disabled={!canFetch}
          >
            {translate("anketa.opsanio.refresh", { defaultMessage: "Refresh" })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiHorizontalRule margin="m" />

      {error && (
        <>
          <EuiCallOut color="danger" iconType="alert" title={error} />
          <EuiSpacer size="m" />
        </>
      )}
      {loading && exists === null && <FormSkeleton />}

      {exists === true && (
        <EuiCallOut
          color="success"
          iconType="check"
          title={translate(`${i18n}.litellm.exists.title`, {
            defaultMessage: "LiteLLM signs users in against Keycloak",
          })}
        >
          <EuiText size="s">
            {translate(`${i18n}.litellm.exists.body`, {
              defaultMessage:
                "The client {{clientId}} is provisioned. Members of {{admin}} sign in as proxy_admin, members of {{user}} as internal_user.",
              values: {
                clientId,
                admin: `${instanceName}/ai-admins`,
                user: `${instanceName}/ai-users`,
              },
            })}
          </EuiText>
        </EuiCallOut>
      )}
      {exists === false && (
        <EuiCallOut
          color="warning"
          iconType="alert"
          title={translate(`${i18n}.litellm.missing.title`, {
            defaultMessage: "LiteLLM has no Keycloak login",
          })}
        >
          <EuiText size="s">
            {translate(`${i18n}.litellm.missing.body`, {
              defaultMessage:
                "No client {{clientId}} exists yet, so the gateway UI falls back to its local username and password. Creating it also binds the ai-admins / ai-users groups to the LiteLLM roles.",
              values: { clientId: `${instanceName}-litellm` },
            })}
          </EuiText>
          <EuiSpacer size="m" />
          {canCreateClient && (
          <EuiButton
            fill
            iconType="plusInCircle"
            onClick={create}
            isLoading={creating}
            disabled={!canFetch}
          >
            {translate(`${i18n}.litellm.create`, {
              defaultMessage: "Create LiteLLM OIDC client",
            })}
          </EuiButton>
          )}
        </EuiCallOut>
      )}

      <EuiSpacer size="m" />
      <EuiText size="xs" color="subdued">
        {translate(`${i18n}.litellm.note`, {
          defaultMessage:
            "The registered redirect must match what the gateway renders as PROXY_BASE_URL:",
        })}{" "}
        <EuiCode>{redirectUri}</EuiCode>
      </EuiText>
      <EuiSpacer size="xs" />
      <EuiText size="xs" color="subdued">
        {translate(`${i18n}.litellm.limits`, {
          defaultMessage:
            "Admin-UI SSO is free for up to 5 users; API callers keep using virtual keys.",
        })}
      </EuiText>
    </EuiPanel>
  );
};

/**
 * Access > OIDC: both Keycloak clients this cluster owns - the Kubernetes one
 * (kubelogin) and, on an AI-enabled VPC, the LiteLLM gateway's own.
 */
export const OpsanioApplianceFlyoutOidc = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => (
  <>
    <KubernetesOidcCard appliance={appliance} />
    <EuiSpacer size="m" />
    <LiteLlmOidcCard appliance={appliance} />
  </>
);

export default OpsanioApplianceFlyoutOidc;
